import type { QuickAddOfferPickerProps } from './quick-add-offer-picker';

export const MAP_QUICK_ADD_PICKER_BASE_KEY = 'pinPopup.quickAddPicker' as const;
export const INVENTORY_QUICK_ADD_PICKER_BASE_KEY = 'quickAdd.picker' as const;

type QuickAddPickerLabels = Pick<
  QuickAddOfferPickerProps,
  | 'title'
  | 'subtitle'
  | 'draftUnitLabel'
  | 'draftSummarySeparator'
  | 'pendingUnitsLabel'
  | 'targetOfferLabel'
  | 'searchPlaceholder'
  | 'createNewLinkLabel'
  | 'changeSelectionLabel'
  | 'cancelChangeLabel'
  | 'backToExistingLabel'
  | 'lastUsedHintLabel'
  | 'destinationLabel'
  | 'creationStatusHintLabel'
  | 'noPreselectionLabel'
  | 'noDraftsLabel'
  | 'noPermissionLabel'
  | 'noResultsLabel'
  | 'createNewLabel'
  | 'createNewHint'
  | 'clientFieldLabel'
  | 'clientFieldPlaceholder'
  | 'salespersonFieldLabel'
  | 'salespersonFieldPlaceholder'
  | 'salespersonFieldLockedHint'
  | 'campaignDurationLabel'
  | 'campaignDurationPlaceholder'
  | 'campaignDurationInvalidRangeLabel'
  | 'confirmExistingLabel'
  | 'confirmNewLabel'
  | 'cancelLabel'
  | 'openFullFormLabel'
  | 'openFullFormHint'
  | 'validationClientRequiredLabel'
>;

export function getInitialSelectedDraftId(
  draftsData: { selectedDraftId: string | null } | undefined,
  lastUsedData: { lastUsedOfferDraft: { offerId: string } | null } | undefined,
) {
  return lastUsedData?.lastUsedOfferDraft?.offerId ?? draftsData?.selectedDraftId ?? null;
}

export function getQuickAddMode(draftsCount: number, selectedDraftId: string | null) {
  if (draftsCount === 0) return 'creating';
  if (selectedDraftId != null) return 'summary';
  return 'changing';
}

/**
 * Resolves the numeric offer id `UnifiedSelectPopover` should auto-apply to
 * Rezerwacja/Opcja when `selectedDraftId` matches an editable draft — the
 * preselected-last-used-draft path applies with no explicit confirm click.
 * Returns `null` when nothing is selected or the match isn't editable.
 */
export function resolvePreselectedOfferId(
  drafts: Array<{ id: string; editable: boolean }>,
  selectedDraftId: string | null,
): number | null {
  const draft = drafts.find((d) => d.id === selectedDraftId && d.editable);
  return draft ? Number(draft.id) : null;
}

export function buildQuickAddPickerLabels(
  translate: (key: string) => string,
  baseKey: string,
): QuickAddPickerLabels {
  return {
    title: translate(`${baseKey}.title`),
    subtitle: translate(`${baseKey}.subtitle`),
    draftUnitLabel: translate(`${baseKey}.draftUnitLabel`),
    draftSummarySeparator: translate(`${baseKey}.draftSummarySeparator`),
    pendingUnitsLabel: translate(`${baseKey}.pendingUnitsLabel`),
    targetOfferLabel: translate(`${baseKey}.targetOfferLabel`),
    searchPlaceholder: translate(`${baseKey}.searchPlaceholder`),
    createNewLinkLabel: translate(`${baseKey}.createNewLinkLabel`),
    changeSelectionLabel: translate(`${baseKey}.changeSelectionLabel`),
    cancelChangeLabel: translate(`${baseKey}.cancelChangeLabel`),
    backToExistingLabel: translate(`${baseKey}.backToExistingLabel`),
    lastUsedHintLabel: translate(`${baseKey}.lastUsedHintLabel`),
    destinationLabel: translate(`${baseKey}.destinationLabel`),
    creationStatusHintLabel: translate(`${baseKey}.creationStatusHintLabel`),
    noPreselectionLabel: translate(`${baseKey}.noPreselectionLabel`),
    noDraftsLabel: translate(`${baseKey}.noDraftsLabel`),
    noPermissionLabel: translate(`${baseKey}.noPermissionLabel`),
    noResultsLabel: translate(`${baseKey}.noResultsLabel`),
    createNewLabel: translate(`${baseKey}.createNewLabel`),
    createNewHint: translate(`${baseKey}.createNewHint`),
    clientFieldLabel: translate(`${baseKey}.clientFieldLabel`),
    clientFieldPlaceholder: translate(`${baseKey}.clientFieldPlaceholder`),
    salespersonFieldLabel: translate(`${baseKey}.salespersonFieldLabel`),
    salespersonFieldPlaceholder: translate(`${baseKey}.salespersonFieldPlaceholder`),
    salespersonFieldLockedHint: translate(`${baseKey}.salespersonFieldLockedHint`),
    campaignDurationLabel: translate(`${baseKey}.campaignDurationLabel`),
    campaignDurationPlaceholder: translate(`${baseKey}.campaignDurationPlaceholder`),
    campaignDurationInvalidRangeLabel: translate(`${baseKey}.campaignDurationInvalidRangeLabel`),
    confirmExistingLabel: translate(`${baseKey}.confirmExistingLabel`),
    confirmNewLabel: translate(`${baseKey}.confirmNewLabel`),
    cancelLabel: translate(`${baseKey}.cancelLabel`),
    openFullFormLabel: translate(`${baseKey}.openFullFormLabel`),
    openFullFormHint: translate(`${baseKey}.openFullFormHint`),
    validationClientRequiredLabel: translate(`${baseKey}.validationClientRequiredLabel`),
  };
}
